import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Candidature, listCandidatures } from '../api/candidatures';
import { ActivityRow, listActivities } from '../api/activities';
import { PaymentRow, listPayments } from '../api/payments';
import { UserRow, listUsers } from '../api/users';
import { listPostsSummary, PostSummary } from '../api/posts';
import { listSchoolsSummary, SchoolSummary } from '../api/schools';

type PeriodKey = 'all' | 'last30' | 'last90' | 'year' | 'lastYear';

const periodLabel: Record<PeriodKey, string> = {
  all: 'Tout',
  last30: '30 derniers jours',
  last90: '90 derniers jours',
  year: 'Année en cours',
  lastYear: 'Année précédente',
};

/** Compte / école : `inactive` exclu ; absence de statut traitée comme actif (données historiques). */
function isAccountActive(status: string | undefined): boolean {
  return status !== 'inactive';
}

/** Poste « actif » pour le pilotage : uniquement les postes approuvés (ouverts). */
function isPostApproved(status: string | undefined): boolean {
  return status === 'approved';
}

function buildUserById(users: UserRow[]): Record<string, UserRow> {
  const m: Record<string, UserRow> = {};
  users.forEach((u) => {
    m[u.id] = u;
  });
  return m;
}

function buildPostById(posts: PostSummary[]): Record<string, PostSummary> {
  const m: Record<string, PostSummary> = {};
  posts.forEach((p) => {
    m[p.id] = p;
  });
  return m;
}

function buildSchoolById(schools: SchoolSummary[]): Record<string, SchoolSummary> {
  const m: Record<string, SchoolSummary> = {};
  schools.forEach((s) => {
    m[s.id] = s;
  });
  return m;
}

/** Candidature prise en compte dans les rapports : utilisateur actif, poste approuvé, école active. */
function candidatureInActiveContext(
  c: Candidature,
  usersById: Record<string, UserRow>,
  postsById: Record<string, PostSummary>,
  schoolsById: Record<string, SchoolSummary>
): boolean {
  const uid = String(c.userId ?? '').trim();
  const pid = String(c.postId ?? '').trim();
  if (!uid || !pid) return false;
  const user = usersById[uid];
  if (!user || !isAccountActive(user.status)) return false;
  const post = postsById[pid];
  if (!post || !isPostApproved(post.status)) return false;
  const sid = String(post.schoolId ?? '').trim();
  if (!sid) return false;
  const school = schoolsById[sid];
  if (!school || !isAccountActive(school.status)) return false;
  return true;
}

function isInPeriod(date: Date | undefined, period: PeriodKey): boolean {
  if (!date || period === 'all') return true;
  const now = new Date();
  const d = date;

  switch (period) {
    case 'last30': {
      const limit = new Date();
      limit.setDate(now.getDate() - 30);
      return d >= limit;
    }
    case 'last90': {
      const limit = new Date();
      limit.setDate(now.getDate() - 90);
      return d >= limit;
    }
    case 'year': {
      return d.getFullYear() === now.getFullYear();
    }
    case 'lastYear': {
      return d.getFullYear() === now.getFullYear() - 1;
    }
    default:
      return true;
  }
}

function computeStatusCounts(
  candidatures: Candidature[],
  period: PeriodKey
) {
  const initial = {
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  };

  return candidatures.reduce((acc, c) => {
    const date = c.date ?? c.createdAt;
    if (!isInPeriod(date, period)) return acc;

    acc.total += 1;
    const status = c.status ?? 'pending';
    if (status === 'approved') acc.approved += 1;
    else if (status === 'rejected') acc.rejected += 1;
    else acc.pending += 1;
    return acc;
  }, initial);
}

function computeActivityStats(
  activities: ActivityRow[],
  period: PeriodKey
) {
  return activities.reduce(
    (acc, a) => {
      const date = a.date ?? a.createdAt;
      if (!isInPeriod(date, period)) return acc;

      acc.count += 1;
      acc.hours += a.hours ?? 0;
      const status = a.status ?? 'pending';
      if (status === 'validated') acc.validated += 1;
      else if (status === 'rejected') acc.rejected += 1;
      else acc.pending += 1;
      return acc;
    },
    {
      count: 0,
      hours: 0,
      pending: 0,
      validated: 0,
      rejected: 0,
    }
  );
}

function computePaymentStats(
  payments: PaymentRow[],
  period: PeriodKey
) {
  return payments.reduce(
    (acc, p) => {
      const date = p.createdAt;
      if (!isInPeriod(date, period)) return acc;

      const amount = p.amount ?? 0;
      acc.count += 1;
      acc.total += amount;
      const status = p.status ?? 'pending';
      if (status === 'paid') {
        acc.paidCount += 1;
        acc.paidTotal += amount;
      } else {
        acc.unpaidCount += 1;
        acc.unpaidTotal += amount;
      }
      return acc;
    },
    {
      count: 0,
      total: 0,
      paidCount: 0,
      paidTotal: 0,
      unpaidCount: 0,
      unpaidTotal: 0,
    }
  );
}

function computeTeacherPaymentStats(
  users: UserRow[] | undefined,
  candidatures: Candidature[] | undefined,
  activities: ActivityRow[] | undefined,
  payments: PaymentRow[] | undefined,
  period: PeriodKey
) {
  if (!users || !candidatures || !activities) {
    return {
      teachersTotal: 0,
      paid: 0,
      pendingPayment: 0,
      noActivities: 0,
    };
  }

  const teachers = users.filter((u) => u.role === 'teacher');
  if (!teachers.length) {
    return {
      teachersTotal: 0,
      paid: 0,
      pendingPayment: 0,
      noActivities: 0,
    };
  }

  const candidatureById: Record<string, Candidature> = {};
  candidatures.forEach((c) => {
    candidatureById[c.id] = c;
  });

  const activitiesInPeriod = activities.filter((a) =>
    isInPeriod(a.date ?? a.createdAt, period)
  );

  const activitiesByTeacher: Record<string, ActivityRow[]> = {};
  activitiesInPeriod.forEach((a) => {
    const candidature = a.candidatureId
      ? candidatureById[a.candidatureId]
      : undefined;
    const teacherId = candidature?.userId;
    if (!teacherId) return;
    if (!activitiesByTeacher[teacherId]) {
      activitiesByTeacher[teacherId] = [];
    }
    activitiesByTeacher[teacherId].push(a);
  });

  const paymentsInPeriod = (payments ?? []).filter((p) =>
    isInPeriod(p.createdAt, period)
  );

  const paymentsByActivityId: Record<string, PaymentRow[]> = {};
  paymentsInPeriod.forEach((p) => {
    if (!p.activityId) return;
    if (!paymentsByActivityId[p.activityId]) {
      paymentsByActivityId[p.activityId] = [];
    }
    paymentsByActivityId[p.activityId].push(p);
  });

  let paid = 0;
  let pendingPayment = 0;
  let noActivities = 0;

  teachers.forEach((t) => {
    const teacherActivities = activitiesByTeacher[t.id] ?? [];
    if (!teacherActivities.length) {
      noActivities += 1;
      return;
    }

    const teacherPayments: PaymentRow[] = [];
    teacherActivities.forEach((a) => {
      const list = a.id ? paymentsByActivityId[a.id] : undefined;
      if (list) {
        teacherPayments.push(...list);
      }
    });

    const hasPaid = teacherPayments.some((p) => p.status === 'paid');

    if (hasPaid) {
      paid += 1;
    } else {
      pendingPayment += 1;
    }
  });

  return {
    teachersTotal: teachers.length,
    paid,
    pendingPayment,
    noActivities,
  };
}

export const ReportsPage: React.FC = () => {
  const { data: candidatures, isLoading: candidaturesLoading } = useQuery<Candidature[]>({
    queryKey: ['reports-candidatures'],
    queryFn: listCandidatures,
  });

  const { data: activities, isLoading: activitiesLoading } = useQuery<ActivityRow[]>({
    queryKey: ['reports-activities'],
    queryFn: () => listActivities(),
  });

  const { data: payments, isLoading: paymentsLoading } = useQuery<PaymentRow[]>({
    queryKey: ['reports-payments'],
    queryFn: () => listPayments(),
  });

  const { data: users, isLoading: usersLoading } = useQuery<UserRow[]>({
    queryKey: ['reports-users'],
    queryFn: listUsers,
  });

  const { data: posts, isLoading: postsLoading } = useQuery<PostSummary[]>({
    queryKey: ['reports-posts'],
    queryFn: listPostsSummary,
  });

  const { data: schools, isLoading: schoolsLoading } = useQuery<SchoolSummary[]>({
    queryKey: ['reports-schools'],
    queryFn: listSchoolsSummary,
  });

  const [period, setPeriod] = React.useState<PeriodKey>('last30');

  const handlePeriodChange = (event: SelectChangeEvent<PeriodKey>) => {
    setPeriod(event.target.value as PeriodKey);
  };

  const reportingDatasets = React.useMemo(() => {
    const u = users ?? [];
    const postList = posts ?? [];
    const sch = schools ?? [];
    const cand = candidatures ?? [];
    const act = activities ?? [];
    const pay = payments ?? [];

    const usersById = buildUserById(u);
    const postsById = buildPostById(postList);
    const schoolsById = buildSchoolById(sch);

    const candidaturesForReport = cand.filter((c) =>
      candidatureInActiveContext(c, usersById, postsById, schoolsById)
    );
    const activeCandidatureIds = new Set(candidaturesForReport.map((c) => c.id));

    const activitiesForReport = act.filter(
      (a) => a.candidatureId && activeCandidatureIds.has(a.candidatureId)
    );
    const activeActivityIds = new Set(activitiesForReport.map((a) => a.id));

    const paymentsForReport = pay.filter(
      (p) => p.activityId && activeActivityIds.has(p.activityId)
    );

    return {
      candidaturesForReport,
      activitiesForReport,
      paymentsForReport,
      usersForTeachers: u.filter((user) => isAccountActive(user.status)),
    };
  }, [users, posts, schools, candidatures, activities, payments]);

  const statusCounts = React.useMemo(
    () => computeStatusCounts(reportingDatasets.candidaturesForReport, period),
    [reportingDatasets, period]
  );

  const activityStats = React.useMemo(
    () => computeActivityStats(reportingDatasets.activitiesForReport, period),
    [reportingDatasets, period]
  );

  const paymentStats = React.useMemo(
    () => computePaymentStats(reportingDatasets.paymentsForReport, period),
    [reportingDatasets, period]
  );

  const teacherPaymentStats = React.useMemo(
    () =>
      computeTeacherPaymentStats(
        reportingDatasets.usersForTeachers,
        candidatures,
        reportingDatasets.activitiesForReport,
        reportingDatasets.paymentsForReport,
        period
      ),
    [reportingDatasets, candidatures, period]
  );

  const contextLoading =
    usersLoading ||
    postsLoading ||
    schoolsLoading ||
    candidaturesLoading ||
    activitiesLoading ||
    paymentsLoading;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h5" gutterBottom>
        Rapports · Candidatures
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
        Les totaux incluent uniquement les éléments actifs : utilisateurs et écoles non désactivés,
        postes au statut approuvé, et candidatures rattachées à cette chaîne. Les activités et
        paiements suivent les candidatures ainsi filtrées ;         les enseignants comptés sont les comptes
        actifs.
      </Typography>
      {contextLoading && (
        <Typography variant="body2" color="text.secondary">
          Chargement des données utilisateurs, écoles et postes…
        </Typography>
      )}

      <Paper sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="subtitle1">
            Période
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="period-label">Période</InputLabel>
            <Select
              labelId="period-label"
              label="Période"
              value={period}
              onChange={handlePeriodChange}
            >
              {Object.entries(periodLabel).map(([key, label]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Candidatures (période)
              </Typography>
              <Typography variant="h5">
                {statusCounts.total}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="overline" color="text.secondary">
                En attente
              </Typography>
              <Typography variant="h5">
                {statusCounts.pending}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Approuvées
              </Typography>
              <Typography variant="h5">
                {statusCounts.approved}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Rejetées
              </Typography>
              <Typography variant="h5">
                {statusCounts.rejected}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Répartition par statut (période sélectionnée)
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Statut</TableCell>
                <TableCell align="right">Nombre</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>En attente</TableCell>
                <TableCell align="right">
                  {statusCounts.pending}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Approuvée</TableCell>
                <TableCell align="right">
                  {statusCounts.approved}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Rejetée</TableCell>
                <TableCell align="right">
                  {statusCounts.rejected}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Rapports · Activités
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Activités (période)
              </Typography>
              <Typography variant="h5">
                {activityStats.count}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Heures totales
              </Typography>
              <Typography variant="h5">
                {activityStats.hours.toLocaleString()}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Validées
              </Typography>
              <Typography variant="h5">
                {activityStats.validated}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Rejetées
              </Typography>
              <Typography variant="h5">
                {activityStats.rejected}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Rapports · Paiements
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Paiements (période)
              </Typography>
              <Typography variant="h5">
                {paymentStats.count}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Montant total
              </Typography>
              <Typography variant="h5">
                {paymentStats.total.toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="overline" color="text.secondary">
                Payés
              </Typography>
              <Typography variant="h5">
                {paymentStats.paidCount}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="overline" color="text.secondary">
                En attente / non payés
              </Typography>
              <Typography variant="h5">
                {paymentStats.unpaidCount}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Rapports · Enseignants (période)
        </Typography>
        {contextLoading && (
          <Typography variant="body2" color="text.secondary">
            Chargement...
          </Typography>
        )}
        {!contextLoading && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="overline" color="text.secondary">
                  Enseignants (total)
                </Typography>
                <Typography variant="h5">
                  {teacherPaymentStats.teachersTotal}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="overline" color="text.secondary">
                  Enseignants payés
                </Typography>
                <Typography variant="h5">
                  {teacherPaymentStats.paid}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="overline" color="text.secondary">
                  Enseignants en attente paiement
                </Typography>
                <Typography variant="h5">
                  {teacherPaymentStats.pendingPayment}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="overline" color="text.secondary">
                  Enseignants sans activité
                </Typography>
                <Typography variant="h5">
                  {teacherPaymentStats.noActivities}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

